import React from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OptimisedImage — drop-in <picture> wrapper that serves AVIF + WebP
 * variants of any image URL via the backend `/api/img` proxy, with the
 * original JPEG as a fallback.
 *
 * Works for:
 *   • External URLs (Shopify CDN, iconicimages.com, etc.) — proxied.
 *   • Internal /api/cloud-media/... or /api/media/... — proxied.
 *   • Local /hero/ /hero-rooms/ etc. — bypassed (they already have
 *     .webp/.avif siblings from the build-step optimiser).
 *
 * Any transcode failure falls through to the original <img src>, so
 * the tile ALWAYS renders even if the proxy is down.
 */
const _isLocalStaticAsset = (src) => {
  if (!src) return true;
  if (src.startsWith('http')) return false;
  // Only paths under /hero/, /hero-rooms/, /artist-heroes/ actually
  // ship pre-built .webp/.avif siblings via the build-step optimiser.
  // Legacy /scraped-artworks/ and /images/ do NOT — routing them here
  // makes the browser fetch dead .avif/.webp URLs that the dev server
  // answers with index.html (200 text/html), producing broken tiles.
  // Those paths must go through the /api/img proxy instead.
  return /^\/(hero|hero-rooms|artist-heroes)\//.test(src);
};

const _buildProxyUrl = (src, width, fmt) => {
  const absolute = src.startsWith('http') ? src : `${API_URL}${src}`;
  return `${API_URL}/api/img?src=${encodeURIComponent(absolute)}&w=${width}&fmt=${fmt}`;
};

/**
 * @param {string}  src        image URL (required)
 * @param {string}  alt        alt text (required)
 * @param {number}  width      target width bucket for the proxy (default 800)
 * @param {string}  className  passed through to the fallback <img>
 * @param {string}  loading    "lazy" | "eager"  (default lazy)
 * @param {string}  fetchPriority "high" | "low" (optional; hero uses high)
 * @param {string}  decoding   "async" | "sync"  (default async)
 * @param {Object}  imgProps   extra <img> props (onLoad, etc.)
 */
const OptimisedImage = ({
  src,
  alt = '',
  width = 800,
  className = '',
  loading = 'lazy',
  fetchPriority,
  decoding = 'async',
  imgProps = {},
  ...rest
}) => {
  if (!src) return null;

  // Local /hero /hero-rooms etc. already have build-step siblings.
  // Point <source> at them so the browser picks AVIF/WebP directly
  // from the static host — no proxy round-trip.
  if (_isLocalStaticAsset(src)) {
    const idx = src.lastIndexOf('.');
    const base = idx > 0 ? src.substring(0, idx) : src;
    const ext = idx > 0 ? src.substring(idx) : '.jpg';
    return (
      <picture {...rest}>
        <source srcSet={`${base}.avif`} type="image/avif" />
        <source srcSet={`${base}.webp`} type="image/webp" />
        <img
          src={src}
          alt={alt}
          className={className}
          loading={loading}
          decoding={decoding}
          {...(fetchPriority ? { fetchPriority } : {})}
          {...imgProps}
        />
      </picture>
    );
  }

  // Anything else — external URLs, /api/cloud-media, /api/media —
  // routes through the backend transcoding proxy.
  const avif = _buildProxyUrl(src, width, 'avif');
  const webp = _buildProxyUrl(src, width, 'webp');
  return (
    <picture {...rest}>
      <source srcSet={avif} type="image/avif" />
      <source srcSet={webp} type="image/webp" />
      <img
        src={src.startsWith('http') ? src : `${API_URL}${src}`}
        alt={alt}
        className={className}
        loading={loading}
        decoding={decoding}
        {...(fetchPriority ? { fetchPriority } : {})}
        {...imgProps}
      />
    </picture>
  );
};

export default OptimisedImage;
